import { useCallback, useMemo, useRef } from 'react';
import moment from 'moment';
import { View } from 'react-big-calendar';
import { useLocalStorageByUser } from 'common/hooks';
import {
  CALENDAR_DATE_FORMAT,
  CALENDAR_NAV_PREV,
  CALENDAR_NAV_TODAY,
  CALENDAR_VIEW_MONTH,
  CALENDAR_VIEW_WEEK,
  CALENDAR_VIEWS,
  CalendarNavAction,
  DEFAULT_CALENDAR_VIEW,
} from 'dashboard409a/common/constants/calendar';

interface UseCalendarStateProps {
  dateStorageKey: string;
  viewStorageKey: string;
}

interface UseCalendarStateReturn {
  calendarDate: Date;
  setCalendarDate: (date: Date) => void;
  calendarView: View;
  isReady: boolean;
  handleCalendarNavigate: (action: CalendarNavAction) => void;
  handleCalendarViewChange: (view: View) => void;
}

export const useCalendarState = ({ dateStorageKey, viewStorageKey }: UseCalendarStateProps): UseCalendarStateReturn => {
  const initialDateRef = useRef(moment().format(CALENDAR_DATE_FORMAT));
  const [storedDate, setStoredDate, isDateReady] = useLocalStorageByUser(
    dateStorageKey,
    initialDateRef.current
  );
  const [storedView, setStoredView, isViewReady] = useLocalStorageByUser<View>(
    viewStorageKey,
    DEFAULT_CALENDAR_VIEW
  );
  const calendarDate = useMemo(() => {
    const parsedDate = moment(storedDate, CALENDAR_DATE_FORMAT, true);
    return parsedDate.isValid() ? parsedDate.toDate() : new Date();
  }, [storedDate]);
  const calendarView = CALENDAR_VIEWS.includes(storedView as typeof CALENDAR_VIEWS[number])
    ? storedView
    : DEFAULT_CALENDAR_VIEW;

  const setCalendarDate = useCallback(
    (date: Date) => setStoredDate(moment(date).format(CALENDAR_DATE_FORMAT)),
    [setStoredDate]
  );

  const handleCalendarNavigate = useCallback((action: CalendarNavAction) => {
    if (action === CALENDAR_NAV_TODAY) {
      setCalendarDate(new Date());
      return;
    }
    const unit = calendarView === CALENDAR_VIEW_MONTH ? CALENDAR_VIEW_MONTH : CALENDAR_VIEW_WEEK;
    const nextDate = action === CALENDAR_NAV_PREV
      ? moment(calendarDate).subtract(1, unit).toDate()
      : moment(calendarDate).add(1, unit).toDate();
    setCalendarDate(nextDate);
  }, [calendarDate, calendarView, setCalendarDate]);

  const handleCalendarViewChange = useCallback((view: View) => {
    setStoredView(view);
  }, [setStoredView]);

  return {
    calendarDate,
    setCalendarDate,
    calendarView,
    isReady: isDateReady && isViewReady,
    handleCalendarNavigate,
    handleCalendarViewChange,
  };
};
